import { useRef } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconRobot, IconTrash, IconX } from '@tabler/icons-react';

import { useDispatch, useSelector } from 'store';
import { clearMessages, closeChatbot, sendMessage, stopStreaming } from 'store/slices/chatbotsSlice';

import MessageList, { EmptyState } from './MessageList';
import ChatInput from './ChatInput';

// ==============================|| CHATBOT DRAWER ||============================== //

const DRAWER_WIDTH = 560;

const ChatbotDrawer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const { open, streaming, messages } = useSelector((state) => state.chatbots);
  const scrollRef = useRef(null);

  const handleClose = () => {
    // Closing mid-stream aborts the in-flight request; history is kept in Redux
    // so reopening restores the conversation within the session.
    if (streaming) dispatch(stopStreaming());
    dispatch(closeChatbot());
  };

  const handleSend = (question) => {
    dispatch(sendMessage(question));
  };

  const handleClear = () => {
    if (streaming) dispatch(stopStreaming());
    dispatch(clearMessages());
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: { sx: { width: matchDownSm ? '100%' : DRAWER_WIDTH, display: 'flex', flexDirection: 'column' } }
      }}
    >
      {/* header */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center'
          }}
        >
          <IconRobot size={22} />
          <Typography variant="h4">Assistant</Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: 'center'
          }}
        >
          {messages.length > 0 && (
            <Tooltip title="Clear conversation">
              <IconButton size="small" onClick={handleClear}>
                <IconTrash size={18} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton onClick={handleClose} aria-label="close chatbot">
              <IconX size={22} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Divider />

      {messages.length > 0 ? (
        <>
          {/* message list */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <PerfectScrollbar containerRef={(el) => (scrollRef.current = el)} style={{ height: '100%' }}>
              <MessageList messages={messages} scrollRef={scrollRef} />
            </PerfectScrollbar>
          </Box>

          {/* input pinned to the bottom once the conversation has content */}
          <ChatInput onSend={handleSend} disabled={streaming} />
        </>
      ) : (
        // empty conversation: center the greeting + input in the middle of the drawer
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, px: 1 }}>
          <EmptyState />
          <ChatInput onSend={handleSend} disabled={streaming} divider={false} />
        </Box>
      )}
    </Drawer>
  );
};

export default ChatbotDrawer;
